
import { IndustryType } from './industry';
import { DOMAIN_CONFIG } from '../config/domainConfig';

export interface SaaSFeature {
    title: string;
    description: string;
    icon: string; // Icon name (e.g. 'Calendar', 'CreditCard')
}

export type SaaSFeatureKey = 
    // Core Modules
    | 'bookings'            // Calendar & Scheduling
    | 'projects'            // Order Management
    | 'crm_module'          // Client Directory
    | 'finance_module'      // Invoicing & Revenue
    | 'marketing_module'    // Newsletters
    | 'workshops_module'    // Events
    | 'legal_module'        // Contracts
    | 'portfolio_module'    // CV & Showcase
    | 'forms_module'        // Form Builder
    | 'files_module'        // File Manager (Standalone)
    | 'contact_module'      // Contact Form
    | 'teams'               // Staff Accounts

    // Infrastructure & Limits
    | 'unlimited_storage'
    | 'api_access'
    
    // Branding & Client Experience
    | 'white_label'         // Remove "Powered By"
    | 'custom_domain'       // Own URL
    | 'client_portal'       // Branded Client Login
    
    // Calendar Tiers
    | 'calendar_basic'      // Phase 1: One-way Sync (App -> GCal)
    | 'calendar_pro'        // Phase 2: Two-way Busy Check + Client Invites
    | 'calendar_enterprise' // Phase 3: Multi-Calendar Routing + Resources
    
    // Integrations
    | 'smtp_integration'    // Custom Email Server
    | 'whatsapp_integration' // Custom WA Gateway
    
    // Advanced
    | 'ai_assistant'
    | 'analytics_advanced';

export interface SaaSPlan {
    id: string;
    name: string;
    description: string;
    price: number;
    currency: string;
    interval: 'month' | 'year';
    stripePriceId: string; // From Stripe Dashboard
    features: SaaSFeatureKey[]; // List of enabled feature keys
    active: boolean;
    isPopular?: boolean;
    storageLimitGB: number; 
}

export interface TenantSubscription {
    status: 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete';
    planId: string;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    currentPeriodEnd: number;
    cancelAtPeriodEnd: boolean;
}

export interface TenantWallet {
    balance: number; // SZoins
    currency: 'SZoins';
    updatedAt: number;
}

export interface TenantUsage {
    email: {
        lastSentAt: number;
        currentHourCount: number;
        hourlyLimit?: number; 
    };
    storage: {
        usedBytes: number; 
        updatedAt: number;
    };
}

export interface SupportRequest {
    id: string;
    tenantId: string;
    tenantName: string;
    requesterId: string;
    requesterEmail: string;
    type: 'DOMAIN_CHANGE' | 'INDUSTRY_CHANGE' | 'FEATURE_REQUEST' | 'OTHER';
    status: 'PENDING' | 'IN_PROGRESS' | 'RESOLVED' | 'REJECTED';
    details: string;
    assignedTo?: string; // Super Admin ID
    createdAt: number;
    updatedAt: number;
}

export interface SaaSLandingContent {
    hero: {
        title: string;
        subtitle: string;
        ctaText: string;
        ctaLink: string;
        badgeText?: string;
    };
    about: {
        title: string;
        description: string;
    };
    // New About Page Content
    aboutPage: {
        title: string;
        content: string; // Long text
        showOnNav: boolean;
    };
    features: SaaSFeature[];
    pricing: {
        title: string;
        description: string;
        tiers: { name: string; price: string; features: string[] }[];
    };
    seo: {
        metaTitle: string;
        metaDescription: string;
        keywords: string[];
    };
    branding: {
        companyName: string;
        address: string;
        contactEmail: string;
    };
}

export const DEFAULT_SAAS_CONTENT: SaaSLandingContent = {
    hero: {
        title: "The Operating System for Creative Studios",
        subtitle: "Automate bookings, streamline payments, and scale your creative business with the world's most advanced studio management platform.",
        ctaText: "Start Free Trial",
        ctaLink: "/register",
        badgeText: "v3.0 Now with AI Copilot"
    },
    about: {
        title: "Built for Professionals",
        description: "From Grammy-winning recording studios to high-volume teaching academies, Janoobi Studios provides the infrastructure to run your business on autopilot."
    },
    aboutPage: {
        title: "About Us",
        content: "Welcome to our platform. We are dedicated to providing the best service possible.",
        showOnNav: true
    },
    features: [
        { title: "Smart Scheduling", description: "Conflict-free booking with timezone detection and Google Calendar sync.", icon: "Calendar" },
        { title: "Financial Suite", description: "Automated invoicing, split payments, and artist commission tracking.", icon: "CreditCard" },
        { title: "Client Portal", description: "Give your clients a white-labeled dashboard to manage their projects.", icon: "Users" },
        { title: "AI & Automation", description: "Auto-draft contracts and summarize project briefs instantly.", icon: "Zap" }
    ],
    pricing: {
        title: "Transparent Pricing",
        description: "Scale your studio with plans designed for growth.",
        tiers: [
            { name: "Starter", price: "$29", features: ["1 Calendar", "Online Booking", "Basic CRM"] },
            { name: "Professional", price: "$79", features: ["Unlimited Calendars", "Team Payouts", "Contracts"] },
            { name: "Enterprise", price: "$199", features: ["White Label", "API Access", "Dedicated Support"] }
        ]
    },
    seo: {
        metaTitle: "Janoobi Studios | The #1 Studio Management Software",
        metaDescription: "All-in-one booking, payments, and project management for audio studios, creative agencies, and tutors.",
        keywords: [
            "Studio Management Software", 
            "Booking System", 
            "SaaS", 
            "Janoobi Studios", 
            "Audio Studio CRM",
            "White Label Booking Software"
        ]
    },
    branding: {
        companyName: "Janoobi Studios",
        address: "London, United Kingdom",
        contactEmail: `hello@${DOMAIN_CONFIG.SAAS_ROOT}`
    }
};
